import { onBeforeUnmount, onMounted, readonly, ref, shallowRef, type Ref, type ShallowRef } from 'vue';

import { fetchUsage, refreshUsage } from '../api/usage';
import type { UsageData } from '../api/types';

export type UsageState = 'idle' | 'loading' | 'success' | 'error';

export interface UseUsageResult {
  readonly state: Readonly<Ref<UsageState>>;
  readonly data: Readonly<ShallowRef<UsageData | null>>;
  readonly lastFetchedAt: Readonly<Ref<number | null>>;
  readonly error: Readonly<Ref<string | null>>;
  readonly isCached: Readonly<Ref<boolean>>;
  /** True while a request is in-flight. */
  readonly loading: Readonly<Ref<boolean>>;
  /** Force-refresh (POST /api/token-usage, bypasses KV cache). */
  readonly refresh: () => Promise<void>;
  /** Soft refresh (GET ?fresh=1, also bypasses KV cache via separate path). */
  readonly refetchFresh: () => Promise<void>;
  /** Start 60s polling; safe to call multiple times (idempotent). */
  readonly startPolling: () => void;
  /** Stop 60s polling. */
  readonly stopPolling: () => void;
  /** Reset state to idle (clears data). */
  readonly reset: () => void;
}

/**
 * Vue 3 composable that owns the dashboard data lifecycle:
 *   * initial fetch on mount
 *   * 60s auto-poll, paused while document.hidden
 *   * AbortController for cancellation (refresh never queues a stale response)
 *   * exposes a read-only surface to the rest of the app
 *
 * We use `shallowRef` for the data payload — `UsageData` is large,
 * deeply-readonly, and never mutated reactively. Components rely on
 * `key={lastFetchedAt.value}` to re-render cards when a new snapshot lands.
 */
export function useUsage(): UseUsageResult {
  const state = ref<UsageState>('idle');
  const data = shallowRef<UsageData | null>(null);
  const lastFetchedAt = ref<number | null>(null);
  const error = ref<string | null>(null);
  const isCached = ref(false);
  const loading = ref(false);

  let inflight: AbortController | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let pollIntervalMs = 60_000;

  function setLoading(value: boolean): void {
    loading.value = value;
    if (value) state.value = state.value === 'error' ? 'loading' : state.value;
  }

  async function runOnce(mode: 'fetch' | 'refresh' | 'fresh'): Promise<void> {
    // Cancel any in-flight request before starting a new one.
    inflight?.abort();
    const ctrl = new AbortController();
    inflight = ctrl;

    setLoading(true);
    if (state.value !== 'success') state.value = 'loading';

    try {
      const fn = mode === 'refresh' ? refreshUsage : fetchUsage;
      const opts = mode === 'fresh' ? { fresh: true } : {};
      const result = await fn(opts, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      data.value = result;
      lastFetchedAt.value = result.fetchedAt;
      isCached.value = result.cached;
      error.value = null;
      state.value = 'success';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = '未知错误';
      }
      state.value = 'error';
    } finally {
      if (inflight === ctrl) {
        inflight = null;
        setLoading(false);
      }
    }
  }

  function refresh(): Promise<void> {
    return runOnce('refresh');
  }

  function refetchFresh(): Promise<void> {
    return runOnce('fresh');
  }

  function startPolling(): void {
    if (pollTimer != null) return;
    const tick = (): void => {
      if (typeof document !== 'undefined' && document.hidden) return;
      // Don't queue another request if we're already loading — wait for it.
      if (loading.value) return;
      // Fire-and-forget; refresh handles its own errors.
      void runOnce('fetch');
    };
    pollTimer = setInterval(tick, pollIntervalMs);
  }

  function stopPolling(): void {
    if (pollTimer != null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function reset(): void {
    inflight?.abort();
    inflight = null;
    stopPolling();
    state.value = 'idle';
    data.value = null;
    lastFetchedAt.value = null;
    error.value = null;
    isCached.value = false;
    loading.value = false;
  }

  function onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (!document.hidden) {
      // On tab refocus, fetch immediately so the user sees fresh numbers.
      if (!loading.value) void runOnce('fetch');
    }
  }

  onMounted(() => {
    void runOnce('fetch').then(() => {
      startPolling();
    });
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  });

  onBeforeUnmount(() => {
    inflight?.abort();
    stopPolling();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  });

  return {
    state: readonly(state),
    data: readonly(data) as Readonly<ShallowRef<UsageData | null>>,
    lastFetchedAt: readonly(lastFetchedAt),
    error: readonly(error),
    isCached: readonly(isCached),
    loading: readonly(loading),
    refresh,
    refetchFresh,
    startPolling,
    stopPolling,
    reset,
  };
}
